import { Clock, Download, FileSpreadsheet, ShieldOff, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { canSeeMoney } from '@/lib/money';
import { useSession } from '@/stores/session';
import {
  useCreateReportSchedule,
  useDeleteReportSchedule,
  useReportSchedules,
} from '../schedules/queries';
import {
  CADENCE_LABEL,
  REPORT_KEY_LABEL,
  type Cadence,
  type ReportKey,
} from '../schedules/types';

interface ExportItem {
  key: string;
  label: string;
  description: string;
  format: 'CSV' | 'JSON';
  url: string;
  /** True → only finance-money roles see this row. */
  moneyOnly?: boolean;
}

/**
 * Wave 13 — Exports CSV downloads + scheduled emailed reports.
 * Time tracking is open to all staff; P&L / Revenue / Payroll are money-only.
 */
const EXPORTS: ExportItem[] = [
  {
    key: 'pnl',
    label: 'Profit & Loss',
    description: 'Monthly income / expense / net (base USD).',
    format: 'CSV',
    url: '/api/command-center/reports/pnl/?format=csv',
    moneyOnly: true,
  },
  {
    key: 'revenue-by-client',
    label: 'Revenue by client',
    description: 'Recognized income grouped by client company (base USD).',
    format: 'CSV',
    url: '/api/command-center/reports/revenue-by-client/?format=csv',
    moneyOnly: true,
  },
  {
    key: 'time-tracking',
    label: 'Time tracking',
    description: 'TimeEntry export — date, project, user, hours, description.',
    format: 'CSV',
    url: '/api/command-center/reports/time-tracking/?format=csv',
  },
  {
    key: 'payroll',
    label: 'Payroll',
    description: 'Payslips across runs (or a specific run with `?run=<id>`).',
    format: 'CSV',
    url: '/api/command-center/reports/payroll/?format=csv',
    moneyOnly: true,
  },
  {
    key: 'channels',
    label: 'Lead inflow by channel',
    description: 'Upwork / LinkedIn / Website / Referral / Outbound counts.',
    format: 'JSON',
    url: '/api/command-center/channels/',
  },
  {
    key: 'dashboard',
    label: 'Executive dashboard snapshot',
    description: 'Pipeline, projects, tracked hours, finance KPIs (if visible).',
    format: 'JSON',
    url: '/api/command-center/dashboard/',
  },
];

const REPORT_KEYS: ReportKey[] = ['pnl', 'revenue_by_client', 'time_tracking', 'payroll'];
const CADENCES: Cadence[] = ['daily', 'weekly', 'monthly'];

export function ExportsPage() {
  const role = useSession((s) => s.role);
  const showMoney = canSeeMoney(role);
  const rows = EXPORTS.filter((e) => !e.moneyOnly || showMoney);
  // Only org admins can manage schedules (matches BE RBAC).
  const canManageSchedules = role === 'founder' || role === 'super_admin';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Exports</h1>
        <p className="text-muted-foreground text-sm">
          On-demand downloads and scheduled emailed reports.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          <ShieldOff className="size-6" />
          No exports available for your role.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {rows.map((e) => (
            <Card key={e.key}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <div>
                  <CardTitle className="text-base">{e.label}</CardTitle>
                  <CardDescription>{e.description}</CardDescription>
                </div>
                <Badge variant="outline">{e.format}</Badge>
              </CardHeader>
              <CardContent>
                <Button asChild size="sm">
                  <a href={e.url} target="_blank" rel="noreferrer" download>
                    {e.format === 'CSV' ? <Download className="size-4" /> : <FileSpreadsheet className="size-4" />}
                    {e.format === 'CSV' ? 'Download CSV' : 'Open JSON'}
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {canManageSchedules ? <SchedulesSection /> : null}
    </div>
  );
}

function SchedulesSection() {
  const schedules = useReportSchedules();
  const create = useCreateReportSchedule();
  const remove = useDeleteReportSchedule();

  const [reportKey, setReportKey] = useState<ReportKey>('pnl');
  const [cadence, setCadence] = useState<Cadence>('weekly');
  const [email, setEmail] = useState('');

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    await create.mutateAsync({
      report_key: reportKey, cadence, recipient_email: email.trim(),
    });
    setEmail('');
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="size-4" /> Scheduled reports
        </CardTitle>
        <CardDescription>
          Email a CSV on a recurring cadence. Failures are logged; the next tick retries.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="grid gap-2 sm:grid-cols-[1fr_1fr_2fr_auto] sm:items-end"
          onSubmit={onCreate}
        >
          <div>
            <label className="text-sm font-medium" htmlFor="schedule-report">Report</label>
            <Select value={reportKey} onValueChange={(v) => setReportKey(v as ReportKey)}>
              <SelectTrigger id="schedule-report"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REPORT_KEYS.map((k) => (
                  <SelectItem key={k} value={k}>{REPORT_KEY_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="schedule-cadence">Cadence</label>
            <Select value={cadence} onValueChange={(v) => setCadence(v as Cadence)}>
              <SelectTrigger id="schedule-cadence"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CADENCES.map((c) => (
                  <SelectItem key={c} value={c}>{CADENCE_LABEL[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="schedule-email">Recipient</label>
            <Input
              id="schedule-email"
              type="email"
              placeholder="reports@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={create.isPending || !email.trim()}>
            Add
          </Button>
        </form>

        {schedules.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : !schedules.data || schedules.data.length === 0 ? (
          <p className="text-muted-foreground text-sm">No scheduled reports yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {schedules.data.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium">{REPORT_KEY_LABEL[s.report_key]}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {CADENCE_LABEL[s.cadence]} · {s.recipient_email}
                    {s.next_run_at ? ` · next ${new Date(s.next_run_at).toLocaleString()}` : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove.mutate(s.id)}
                  disabled={remove.isPending}
                  aria-label={`Delete schedule ${s.id}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
